/* ═══════════════════════════════════════════════════════════════
   ALTERLY — WebGL2 GPU Render Backend
   Production GPU acceleration for image processing & preview shaders.
   ═══════════════════════════════════════════════════════════════ */

import type {
  RenderBackend,
  ShaderPassDescriptor,
  TextureHandle,
} from '../types';
import { generateStableId } from '../../domain/id';

const DEFAULT_VERTEX_SHADER = `#version 300 es
in vec2 a_position;
out vec2 v_uv;

void main() {
  // Map from [-1, 1] clip space to [0, 1] texture coordinates
  v_uv = (a_position + vec2(1.0)) * 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

export class WebGL2Backend implements RenderBackend {
  readonly name = 'webgl2' as const;
  readonly isAvailable: boolean;

  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGL2RenderingContext | null = null;
  private quadVao: WebGLVertexArrayObject | null = null;
  private quadBuffer: WebGLBuffer | null = null;
  private programCache = new Map<string, WebGLProgram>();
  private activeTextures = new Set<WebGLTexture>();

  constructor() {
    if (typeof document !== 'undefined') {
      try {
        this.canvas = document.createElement('canvas');
        this.canvas.width = 1;
        this.canvas.height = 1;
        this.gl = this.canvas.getContext('webgl2', {
          alpha: true,
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
        });
        this.isAvailable = !!this.gl;
        if (this.gl) {
          this.initQuad();
        }
      } catch {
        this.isAvailable = false;
      }
    } else {
      this.isAvailable = false;
    }
  }

  private initQuad(): void {
    const gl = this.gl!;
    this.quadVao = gl.createVertexArray();
    gl.bindVertexArray(this.quadVao);

    this.quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);

    // Full screen 2-triangle quad
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1,
    ]);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    gl.bindVertexArray(null);
  }

  createTexture(
    source: ImageBitmap | HTMLImageElement | HTMLCanvasElement | ImageData,
    width?: number,
    height?: number
  ): TextureHandle {
    const gl = this.gl;
    if (!gl) {
      throw new Error('WebGL2 context is not available');
    }

    const tex = gl.createTexture();
    if (!tex) throw new Error('Failed to allocate WebGL texture');

    this.activeTextures.add(tex);
    gl.bindTexture(gl.TEXTURE_2D, tex);

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    const w = width ?? (
      typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement
        ? (source.naturalWidth || source.width)
        : source.width
    );
    const h = height ?? (
      typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement
        ? (source.naturalHeight || source.height)
        : source.height
    );

    if (source instanceof ImageData) {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA8,
        w,
        h,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source.data
      );
    } else {
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA8,
        w,
        h,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        source
      );
    }

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindTexture(gl.TEXTURE_2D, null);

    const textureId = generateStableId('node');
    let disposed = false;

    return {
      id: textureId,
      width: w,
      height: h,
      nativeHandle: tex,
      dispose: () => {
        if (!disposed) {
          disposed = true;
          this.activeTextures.delete(tex);
          gl.deleteTexture(tex);
        }
      },
    };
  }

  private getOrCompileProgram(vertexSrc: string, fragmentSrc: string): WebGLProgram {
    const gl = this.gl!;
    const cacheKey = `${vertexSrc}__SPLIT__${fragmentSrc}`;
    const cached = this.programCache.get(cacheKey);
    if (cached) return cached;

    // Enforce bounded shader program cache (max 100 programs)
    if (this.programCache.size >= 100) {
      const oldestKey = this.programCache.keys().next().value;
      if (oldestKey) {
        const oldProgram = this.programCache.get(oldestKey);
        if (oldProgram) {
          gl.deleteProgram(oldProgram);
        }
        this.programCache.delete(oldestKey);
      }
    }

    const vs = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vs, vertexSrc);
    gl.compileShader(vs);
    if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
      const err = gl.getShaderInfoLog(vs);
      gl.deleteShader(vs);
      throw new Error(`Vertex shader compilation failed: ${err}`);
    }

    const fs = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fs, fragmentSrc);
    gl.compileShader(fs);
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      const err = gl.getShaderInfoLog(fs);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      throw new Error(`Fragment shader compilation failed: ${err}`);
    }

    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.bindAttribLocation(program, 0, 'a_position');
    gl.linkProgram(program);

    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const err = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`Shader program link failed: ${err}`);
    }

    this.programCache.set(cacheKey, program);
    return program;
  }

  async executeShaderPass(
    descriptor: ShaderPassDescriptor,
    outputWidth: number,
    outputHeight: number
  ): Promise<TextureHandle> {
    const gl = this.gl;
    if (!gl) throw new Error('WebGL2 context unavailable');

    const vertexSrc = descriptor.vertexSource || DEFAULT_VERTEX_SHADER;
    const fragmentSrc = descriptor.fragmentSource;
    const program = this.getOrCompileProgram(vertexSrc, fragmentSrc);

    // 1. Create target texture
    const targetTex = gl.createTexture();
    if (!targetTex) throw new Error('Failed to create target WebGL texture');
    this.activeTextures.add(targetTex);

    gl.bindTexture(gl.TEXTURE_2D, targetTex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA8,
      outputWidth,
      outputHeight,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // 2. Setup FBO
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      targetTex,
      0
    );

    const fboStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    if (fboStatus !== gl.FRAMEBUFFER_COMPLETE) {
      gl.deleteFramebuffer(fbo);
      gl.deleteTexture(targetTex);
      throw new Error(`FBO is incomplete: status 0x${fboStatus.toString(16)}`);
    }

    gl.viewport(0, 0, outputWidth, outputHeight);
    gl.useProgram(program);

    // 3. Bind uniforms
    let textureUnit = 0;
    if (descriptor.uniforms) {
      for (const [name, val] of Object.entries(descriptor.uniforms)) {
        const loc = gl.getUniformLocation(program, name);
        if (!loc) continue;

        if (typeof val === 'number') {
          gl.uniform1f(loc, val);
        } else if (typeof val === 'boolean') {
          gl.uniform1i(loc, val ? 1 : 0);
        } else if (Array.isArray(val) || (typeof Float32Array !== 'undefined' && val instanceof Float32Array)) {
          if (val.length === 2) gl.uniform2fv(loc, val);
          else if (val.length === 3) gl.uniform3fv(loc, val);
          else if (val.length === 4) gl.uniform4fv(loc, val);
          else if (val.length === 9) gl.uniformMatrix3fv(loc, false, val);
          else if (val.length === 16) gl.uniformMatrix4fv(loc, false, val);
        } else if (val && typeof val === 'object') {
          // Check if TextureHandle or ImageValue
          const texHandle = ('texture' in val && (val as { texture: TextureHandle }).texture)
            ? (val as { texture: TextureHandle }).texture
            : ('nativeHandle' in val ? (val as TextureHandle) : null);

          if (texHandle && texHandle.nativeHandle) {
            gl.activeTexture(gl.TEXTURE0 + textureUnit);
            gl.bindTexture(gl.TEXTURE_2D, texHandle.nativeHandle as WebGLTexture);
            gl.uniform1i(loc, textureUnit);
            textureUnit++;
          }
        }
      }
    }

    // Pass default resolution uniform if shader asks for it
    const resLoc = gl.getUniformLocation(program, 'u_resolution');
    if (resLoc) {
      gl.uniform2f(resLoc, outputWidth, outputHeight);
    }

    // 4. Draw quad
    gl.bindVertexArray(this.quadVao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);

    // 5. Cleanup FBO binding
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fbo);

    let disposed = false;
    return {
      id: generateStableId('node'),
      width: outputWidth,
      height: outputHeight,
      nativeHandle: targetTex,
      dispose: () => {
        if (!disposed) {
          disposed = true;
          this.activeTextures.delete(targetTex);
          gl.deleteTexture(targetTex);
        }
      },
    };
  }

  renderToCanvas(texture: TextureHandle, targetCanvas: HTMLCanvasElement, maxDimension?: number): void {
    const gl = this.gl;
    if (!gl) return;

    let targetWidth = texture.width;
    let targetHeight = texture.height;

    if (maxDimension && maxDimension > 0) {
      const maxSide = Math.max(texture.width, texture.height);
      if (maxSide > maxDimension) {
        const scale = maxDimension / maxSide;
        targetWidth = Math.max(1, Math.round(texture.width * scale));
        targetHeight = Math.max(1, Math.round(texture.height * scale));
      }
    }

    targetCanvas.width = targetWidth;
    targetCanvas.height = targetHeight;

    const ctx = targetCanvas.getContext('2d');
    if (!ctx) return;

    this.readPixels(texture).then((pixels) => {
      if (targetWidth !== texture.width || targetHeight !== texture.height) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = texture.width;
        tempCanvas.height = texture.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
          const imgData = new ImageData(pixels as unknown as ImageDataArray, texture.width, texture.height);
          tempCtx.putImageData(imgData, 0, 0);
          ctx.drawImage(tempCanvas, 0, 0, targetWidth, targetHeight);
        }
      } else {
        const imgData = new ImageData(pixels as unknown as ImageDataArray, texture.width, texture.height);
        ctx.putImageData(imgData, 0, 0);
      }
    });
  }

  async readPixels(texture: TextureHandle): Promise<Uint8ClampedArray> {
    const gl = this.gl;
    if (!gl) throw new Error('WebGL2 context unavailable');

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture.nativeHandle as WebGLTexture,
      0
    );

    const buffer = new Uint8Array(texture.width * texture.height * 4);
    gl.readPixels(
      0,
      0,
      texture.width,
      texture.height,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      buffer
    );

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fbo);

    return new Uint8ClampedArray(buffer.buffer);
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;

    for (const tex of this.activeTextures) {
      gl.deleteTexture(tex);
    }
    this.activeTextures.clear();

    for (const program of this.programCache.values()) {
      gl.deleteProgram(program);
    }
    this.programCache.clear();

    if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
    if (this.quadVao) gl.deleteVertexArray(this.quadVao);

    const loseContext = gl.getExtension('WEBGL_lose_context');
    loseContext?.loseContext();
    this.gl = null;
    this.canvas = null;
  }
}
